import { areasGateway } from "@/gateways/areasGateway";
import type { MutationCallbacks } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { ref, type Ref } from "vue";
import type { Area } from "./types";

export function useNewArea() {
  const area: Ref<Area> = ref({
    id: window.crypto.randomUUID(),
    name: "",
    coordinates: [],
  });

  return { area };
}

export function useGetAreas() {
  const query = useQuery({
    queryKey: ["areas"],
    queryFn: () => areasGateway.listAreas(),
  });

  return { ...query, getAreasStatus: query.status, areas: query.data };
}

export function useGetArea(id: string) {
  const query = useQuery({
    queryKey: ["area", id],
    queryFn: () => areasGateway.getArea(id),
  });

  return { ...query, getAreaStatus: query.status, area: query.data };
}

export function useUpdateArea() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (area: Area) => areasGateway.updateArea(area),
    onSuccess: (area: Area) => {
      queryClient.invalidateQueries({ queryKey: ["areas"] });
      queryClient.setQueryData(["area", area.id], area);
      queryClient.invalidateQueries({ queryKey: ["areaHouses", area.id] });
    },
  });

  return {
    ...mutation,
    updateAreaStatus: mutation.status,
    updateArea: mutation.mutate,
  };
}

export function useCreateArea(callbacks: MutationCallbacks<Area> = {}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (area: Area) => areasGateway.createArea(area),
    onSuccess: (area: Area) => {
      queryClient.invalidateQueries({ queryKey: ["areas"] });
      queryClient.setQueryData(["area", area.id], area);

      if (callbacks.onSuccess) callbacks.onSuccess(area);
    },
  });

  return {
    ...mutation,
    createAreaStatus: mutation.status,
    createArea: mutation.mutate,
  };
}

export function useDeleteArea() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (area: Area) => areasGateway.deleteArea(area),
    onSuccess: (_, area: Area) => {
      queryClient.invalidateQueries({ queryKey: ["areas"] });
      queryClient.invalidateQueries({ queryKey: ["area", area.id] });
    },
  });

  return {
    ...mutation,
    deleteAreaStatus: mutation.status,
    resetDeleteArea: mutation.reset,
    deleteArea: mutation.mutate,
  };
}
